import LogoutModal from "@app/components/logout-modal";
import MenuLinkItem from "@app/components/menu-link-item";
import { RoutePath, MenuList } from "@app/constants";
import LogoutIcon from "@app/icons/logout";
import UserIcon from "@app/icons/user";
import { openPortalDialog } from "@app/services/modal.service";
import { clearUser, selectAuth } from "@app/store/auth";
import { getHeaderLabel } from "@app/utils/layout";
import { clearLocalStorage } from "@app/utils/util";
import { Images } from "@assets/images";
import useOnClickOutside from "@core/hooks/use-on-click-outside.hook";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { addToast } from "@app/components/toast/toast.service";
import AuthorBankService from "@app/services/http/author-bank.service";

function MainLayout() {
  const menuRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isExpandMenu, setIsExpandMenu] = useState(true);

  const [title, setTitle] = useState("");

  const userInfo = useSelector(selectAuth);

  useEffect(() => {
    setTitle(getHeaderLabel(location.pathname));
  }, [location]);

  // Check for pending author bank approvals on mount
  useEffect(() => {
    const checkPendingApprovals = async () => {
      try {
        const response = await AuthorBankService.getPendingCount();
        const pendingCount = response?.data?.count || 0;

        if (pendingCount > 0) {
          addToast({ text: `${pendingCount} yêu cầu liên kết ngân hàng đang chờ duyệt`, status: "warn", position: "top-right", removeOnNextNavigating: false });
        }
      } catch (error) {
        // Failed to fetch pending count - silently ignore
      }
    };

    checkPendingApprovals();
  }, []);

  const onLogoClick = () => {
    navigate(`/${RoutePath.BOOK_MANAGEMENT}`);
  };

  const onLogoutClick = () => {
    const logoutModalObs = openPortalDialog(LogoutModal);

    logoutModalObs.afterClosed().subscribe((data) => {
      if (data?.isAccept) {
        clearLocalStorage();
        dispatch(clearUser());
        navigate(RoutePath.SIGN_IN);
      }
    });
  };

  const renderMenu = () => {
    if (!MenuList.length) return;

    return MenuList.map((item, index) => {
      if (!item.roles?.length) {
        return (
          <MenuLinkItem
            key={index}
            linkTo={item.linkTo}
            icon={item.icon}
            label={isExpandMenu ? item.label : ""}
          />
        );
      }

      if (item.roles.length && userInfo.role) {
        return (
          item.roles.includes(userInfo.role) && (
            <MenuLinkItem
              key={index}
              linkTo={item.linkTo}
              icon={item.icon}
              label={isExpandMenu ? item.label : ""}
            />
          )
        );
      }
    });
  };

  useOnClickOutside(menuRef, () => {
    setIsMenuOpen(false);
  });

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleProfileClick = () => {
    setIsMenuOpen(false);
    navigate(`/${RoutePath.ACCOUNT_INFO}`);
  };

  const handleExpandMenu = () => {
    setIsExpandMenu((pre) => !pre);
  };

  return (
    <div className="bg-color w-full h-screen">
      <div
        className={clsx(
          `sticky top-0 left-0 w-full h-[100px] bg-white ${isExpandMenu ? "pl-[280px]" : "pl-[110px]"}`
        )}
      >
        <div className="pl-[48px] pr-12 py-5 h-full flex justify-between items-center">
          <div className="font-medium text-[#343C6A] text-[28px]">{title}</div>
          <div onClick={toggleMenu}>
            <div className="w-12 h-12 rounded-full cursor-pointer">
              {!!userInfo.id && (
                <img
                  className="w-12 h-12 rounded-full object-cover"
                  alt=""
                  src={userInfo.thumbnail}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {isMenuOpen && (
        <div
          ref={menuRef}
          className="absolute top-16 right-20 mt-2 w-56 bg-white rounded-lg shadow-lg z-10"
        >
          <div className="p-4 flex items-center">
            <div>
              <img
                className="w-12 h-12 rounded-full object-cover"
                src={userInfo.thumbnail}
                alt=""
              />
            </div>
            <div className="ml-2">
              <div className="font-semibold">{userInfo.fullName}</div>
              <div className="text-[12px] text-gray-500">{userInfo.role}</div>
            </div>
          </div>
          <ul className="border-t border-gray-200">
            <li
              className="cursor-pointer p-2 hover:bg-gray-100 flex"
              onClick={handleProfileClick}
            >
              <div className="mr-4">
                <UserIcon />
              </div>
              <div>Thông tin cá nhân</div>
            </li>
            <li
              className="cursor-pointer p-2 hover:bg-gray-100 flex"
              onClick={onLogoutClick}
            >
              <div className="mr-4">
                <LogoutIcon />
              </div>
              <div>Đăng xuất</div>
            </li>
          </ul>
        </div>
      )}
      <div
        className={clsx(
          `h-[calc(100vh-100px)] overflow-y-auto ${isExpandMenu ? "ml-[280px]" : "ml-[110px]"}`
        )}
      >
        <Outlet />
      </div>
      <div
        className={clsx(
          `fixed top-0 left-0 h-screen bg-white ${isExpandMenu ? "w-[280px]" : "w-[110px]"}`
        )}
      >
        <div className="mt-[34px] flex justify-between items-center gap-[11px] px-[25px]">
          <img
            className="cursor-pointer w-28"
            src={Images.LogoText.default}
            alt=""
            onClick={onLogoClick}
          />
          <img
            className="text-red-300 w-[28px] cursor-pointer p-[9px] bg-[#EFF1F9] rounded-[18px]"
            src={
              isExpandMenu
                ? Images.ArrowLeft.default
                : Images.ArrowRight.default
            }
            alt=""
            onClick={handleExpandMenu}
          />
        </div>
        <div
          className={clsx(
            "mt-16 w-full px-[25px] flex flex-col h-[calc(100vh-130px)] pb-6 justify-between overflow-y-auto",
            {
              "!px-[21px]": !isExpandMenu,
            }
          )}
        >
          <div className="flex flex-col gap-5">{renderMenu()}</div>
        </div>
      </div>
    </div>
  );
}

export default MainLayout;
